import {BoardColumnHeader} from '@github-ui/board/frames'
import type {BoardColumn} from '@github-ui/board-core'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'

import styles from './LabelColumnHeader.module.css'
import type {BoardColumnLabels} from './proto-to-board-payload-mapper'

/**
 * A column heading for a board grouped by label: the label's colour beside its name, and
 * the column's count.
 *
 * The name is the server's `nameHtml` where the response carried it, because that is where
 * a label's emoji lives -- a label called `:bug: bug` reads as its shortcode otherwise. A
 * label without it is named by its plain text: the name is user-controlled, so only the
 * server-rendered field may be rendered as markup. Either way it says the same thing
 * `column.label` says, which is what every cell in the column is named after.
 *
 * The swatch is decorative, which `BoardColumnHeader` states for it. The colour is the
 * label's only distinguishing mark here, and the name beside it already says which label
 * this is, so nothing is carried by colour alone.
 *
 * A column with no label behind it -- the `"No label"` bucket, and any column whose label
 * did not reach the browser -- gets the plain heading. There is no colour to draw, and a
 * neutral swatch would read as a label whose colour happens to be grey.
 */
export function LabelColumnHeader({column, columnLabels}: {column: BoardColumn; columnLabels: BoardColumnLabels}) {
  const label = columnLabels.get(column.key)

  if (label === undefined) return <BoardColumnHeader column={column} />

  return (
    <BoardColumnHeader
      column={column}
      leadingVisual={
        <span data-testid="label-color-swatch" className={styles.swatch} style={{backgroundColor: `#${label.color}`}} />
      }
    >
      {label.nameHtml ? <VerifiedHTMLText as="span" html={label.nameHtml} /> : label.name}
    </BoardColumnHeader>
  )
}
